import { OverlayScrollArea, SolidStatusPill } from '@odo/shared-ui';
import React, { useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useSearchScope } from '../context/CommandPaletteContext';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useVideoProgress } from '../hooks/useVideoProgress';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import type { VideoItemData } from '../providers/videosProvider';
import { EditResourceButton } from '../authoring/AuthoringButtons';
import InternalBadge from '../components/InternalBadge';
import PinButton from '../components/PinButton';
import ProgressBar from '../components/ProgressBar';
import ResourceDetailLayout from '../components/ResourceDetailLayout';
import ResourceKindIcon from '../components/ResourceKindIcon';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import RichTextBody from '../components/RichTextBody';
import WatchStatus from '../components/WatchStatus';
import YouTubePlayer from '../components/YouTubePlayer';
import { ResourceEmpty, ResourceError, ResourceLoading } from '../components/ResourceStates';
import type { ContentAudience } from '../utils/audience';
import type { ResourceEntryKind } from '../utils/resourceIndex';
import { formatResourceDate } from '../utils/resourceIndex';
import { richTextToPlainText } from '../utils/richText';
import { youTubeThumbnail } from '../utils/youtube';

/** Guides shown under "Related documents". */
const RELATED_DOC_LIMIT = 3;

/** Questions shown under "Related FAQs". */
const RELATED_FAQ_LIMIT = 3;

/** Words shorter than this carry no meaning for matching ("to", "the", "an"). */
const MIN_TOKEN_LENGTH = 4;

/** Breaks a title into lowercase words worth matching on. */
function tokenize(text: string): string[] {
    return [
        ...new Set(
            text
                .toLowerCase()
                .split(/[^a-z0-9]+/)
                .filter((word) => word.length >= MIN_TOKEN_LENGTH)
        )
    ];
}

/** Row shared by the Related documents and Related FAQs sections. */
const RelatedResourceRow: React.FC<{
    to: string;
    kind: ResourceEntryKind;
    title: string;
    rightLabel: string;
    audience: ContentAudience;
}> = ({ to, kind, title, rightLabel, audience }) => (
    <Link
        to={to}
        className="flex items-center gap-3 border border-gray-200 rounded-xl px-3.5 py-3 hover:border-gray-800 transition-colors"
    >
        <ResourceKindIcon kind={kind} />
        <span className="min-w-0 flex-1 text-sm font-bold text-gray-800 leading-snug truncate">
            {title}
        </span>
        <InternalBadge audience={audience} />
        <span className="flex-shrink-0 text-xs text-gray-400">{rightLabel}</span>
    </Link>
);

const PlaylistRailRow: React.FC<{
    video: VideoItemData;
    isCurrent: boolean;
    basePath: string;
}> = ({ video, isCurrent, basePath }) => {
    const { progress, percentFor } = useVideoProgress();
    return (
        <Link
            to={`${basePath}/videos/${video.slug}`}
            aria-current={isCurrent ? 'page' : undefined}
            className={`flex gap-3 items-center p-2 rounded-lg transition-colors ${
                isCurrent ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
        >
            <img
                src={youTubeThumbnail(video.videoUrl)}
                alt=""
                className="w-[72px] h-11 flex-shrink-0 rounded object-cover bg-gray-200"
            />
            <div className="min-w-0 flex flex-col gap-1">
                <span className="text-xs font-bold text-gray-800 leading-snug line-clamp-2">
                    {video.title}
                </span>
                <InternalBadge audience={video.audience} className="self-start" />
                <WatchStatus
                    completed={progress[video.slug]?.completed ?? false}
                    percent={percentFor(video.slug)}
                    size="2xs"
                    align="start"
                    barClassName="w-16"
                />
            </div>
        </Link>
    );
};

/** Video detail: breadcrumb, player card left, search + full-height playlist card right. */
export const VideoDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { basePath } = useResourcesConfig();
    const { videos, helpDocuments, faqItems, isLoading, error, refresh } = useResourcesLibrary();
    const { progress, percentFor, report } = useVideoProgress();

    const video = useMemo(() => videos.find((item) => item.slug === slug), [videos, slug]);

    useResourcePageTitle(video?.title ?? 'Videos');
    useSearchScope('video');

    const playlist = useMemo(() => {
        if (!video) return [];
        // The playlist is this video's first category, in publication order —
        // the same grouping the videos list shows.
        const primaryCategory = video.category[0];
        const pool = primaryCategory
            ? videos.filter((item) => item.category.includes(primaryCategory))
            : videos;
        return [...pool].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
    }, [videos, video]);

    const relatedDocs = useMemo(() => {
        if (!video) return [];
        return helpDocuments
            .filter((doc) => doc.category.some((cat) => video.category.includes(cat)))
            .slice(0, RELATED_DOC_LIMIT);
    }, [helpDocuments, video]);

    // Category overlap first; then FAQs scored by how many title words they
    // mention (exact tag agreement between the two taxonomies is rare).
    const relatedFaqs = useMemo(() => {
        if (!video) return [];
        const videoCategories = video.category.map((cat) => cat.toLowerCase());
        const byCategory = faqItems.filter((item) =>
            item.category.some((cat) => videoCategories.includes(cat.toLowerCase()))
        );
        if (byCategory.length >= RELATED_FAQ_LIMIT) {
            return byCategory.slice(0, RELATED_FAQ_LIMIT);
        }

        const tokens = tokenize(`${video.title} ${video.category.join(' ')}`);
        const seen = new Set(byCategory.map((item) => item.id));
        const byTitleWords = faqItems
            .filter((item) => !seen.has(item.id))
            .map((item) => {
                const haystack =
                    `${item.question} ${richTextToPlainText(item.answer)}`.toLowerCase();
                return {
                    item,
                    score: tokens.filter((token) => haystack.includes(token)).length
                };
            })
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score)
            .map(({ item }) => item);

        return [...byCategory, ...byTitleWords].slice(0, RELATED_FAQ_LIMIT);
    }, [faqItems, video]);

    const handleProgress = useCallback(
        (seconds: number, duration: number) => {
            if (slug) report(slug, seconds, duration);
        },
        [report, slug]
    );

    if (isLoading && videos.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceLoading label="Loading video…" />
            </div>
        );
    }

    if (error && videos.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceError message={error} onRetry={() => refresh(true)} />
            </div>
        );
    }

    if (!video) {
        return (
            <div className="flex flex-col min-h-0 flex-1">
                <ResourcesPageHeader title="Videos" searchPlaceholder="Search videos…" />
                <ResourceEmpty
                    title="Video not found"
                    description="This video might have been removed, or the link is out of date."
                />
            </div>
        );
    }

    const percent = percentFor(video.slug);
    const completed = progress[video.slug]?.completed ?? false;
    const positionIndex = playlist.findIndex((item) => item.slug === video.slug);

    return (
        <ResourceDetailLayout
            crumbs={[
                { label: 'Resources', to: basePath },
                { label: 'Videos', to: `${basePath}/videos` },
                { label: video.title }
            ]}
            searchPlaceholder="Search videos…"
            rail={
                playlist.length > 0
                    ? {
                          title: video.category[0] ?? 'All videos',
                          positionLabel:
                              positionIndex >= 0
                                  ? `${positionIndex + 1} of ${playlist.length}`
                                  : undefined,
                          children: playlist.map((item) => (
                              <PlaylistRailRow
                                  key={item.id}
                                  video={item}
                                  isCurrent={item.slug === video.slug}
                                  basePath={basePath}
                              />
                          ))
                      }
                    : null
            }
        >
            <div className="h-full min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-5">
                <div className="flex-shrink-0 rounded-xl overflow-hidden bg-gray-900">
                    <YouTubePlayer
                        videoUrl={video.videoUrl}
                        title={video.title}
                        onProgress={handleProgress}
                        className="aspect-video w-full"
                    />
                    {percent > 0 && <ProgressBar percent={percent} className="rounded-none" />}
                </div>

                {/* Everything under the player scrolls inside the card's bounds. */}
                <OverlayScrollArea
                    className="flex-1 min-h-0 -mr-3"
                    contentClassName="pr-3"
                    innerClassName="flex flex-col gap-5 pb-1"
                >
                    <div className="flex flex-col gap-3.5">
                        <div className="flex items-center justify-between gap-3">
                            <h1 className="min-w-0 flex-1 text-lg font-bold text-gray-800 leading-snug">
                                {video.title}
                            </h1>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <InternalBadge audience={video.audience} />
                                <EditResourceButton kind="video" item={video} name={video.title} />
                                <PinButton kind="video" id={video.id} title={video.title} />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2.5">
                                <span className="text-xs leading-none text-gray-400">
                                    Released {formatResourceDate(video.createdAt)}
                                </span>
                                <span className="text-xs leading-none text-gray-300">·</span>
                                <WatchStatus completed={completed} percent={percent} />
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {video.category.map((cat) => (
                                    <SolidStatusPill key={cat} value={cat} />
                                ))}
                            </div>
                        </div>
                        {video.description && (
                            <RichTextBody
                                value={video.description}
                                fallback="plain"
                                className="prose prose-sm prose-slate max-w-none text-sm leading-relaxed text-gray-600 max-w-[660px]"
                            />
                        )}
                    </div>

                    {relatedDocs.length > 0 && (
                        <div className="border-t border-gray-200 pt-5 flex flex-col gap-3">
                            <h2 className="text-sm font-bold text-gray-800">Related documents</h2>
                            <div className="flex flex-col gap-2">
                                {relatedDocs.map((doc) => (
                                    <RelatedResourceRow
                                        key={doc.id}
                                        to={`${basePath}/help-center/${doc.slug}`}
                                        kind="document"
                                        title={doc.title}
                                        rightLabel="Help doc"
                                        audience={doc.audience}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* The anchor opens the FAQ page with the row expanded. */}
                    {relatedFaqs.length > 0 && (
                        <div className="border-t border-gray-200 pt-5 flex flex-col gap-3">
                            <h2 className="text-sm font-bold text-gray-800">Related FAQs</h2>
                            <div className="flex flex-col gap-2">
                                {relatedFaqs.map((item) => (
                                    <RelatedResourceRow
                                        key={item.id}
                                        to={`${basePath}/faq#faq-${item.id}`}
                                        kind="faq"
                                        title={item.question}
                                        rightLabel="FAQ"
                                        audience={item.audience}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </OverlayScrollArea>
            </div>
        </ResourceDetailLayout>
    );
};

export default VideoDetailPage;
